import React, { useEffect, useState, useCallback, MouseEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty, uniqueId } from 'lodash';
import filesize from "filesize";
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachIcon from '@mui/icons-material/AttachFile';
import DoneIcon from '@mui/icons-material/Done';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Typography,
  IconButton,
  TextField,
  FormControl,
  Dialog,
  Box,
  FormHelperText
} from '@mui/material';

import API from '../../../axios/api';
import { i18n } from '../../../translate/i18n';
import * as actions from "../../../store/actions";

import Autocomplete from '../../../UI/Autocomplete/Autocomplete';

import Upload from '../../../UI/Upload';
import FileList from '../../../UI/FileList';
import confirm from '../../../UI/Confirm/confirm';
import Spinner from '../../../components/Spinner/index';
import resizeFile from '../../../utils/imageResizer';
import { map as asyncMap } from 'p-iteration';
import { IUser } from '../../../models/iuser';
import { IAutocompleteOption } from '../../../models/iautocompleteOption';
import { IAction } from '../../../models/iaction';
import { IQuestion } from '../../../models/iquestion';
import { IFile } from '../../../UI/FileList';
import processUpload from '../../../utils/processUpload';
import { IServiceQuestion } from '../../../models/iserviceQuestion';
import { EUpdateDraftType, IUpdateServiceDraftBody } from '../CreateService/Checklist/Checklist';
import ObjectID from 'bson-objectid';

type Props = {
  isOpen: boolean,
  onClose: CallableFunction,
  isNewData: boolean,
  action: IAction | null,
  question: IQuestion | IServiceQuestion,
  serviceId?: string,
  createOrUpdateDraft: (body: IUpdateServiceDraftBody) => Promise<void>,
};

interface IRequestActionBody {
  _id: string,
  actionText: string,
  status: string,
  deadline: Date,
  files: string[],
  question: string,
  questionText: string,
  questionGroupName?: string,
  accountables: {_id: string|null, name: string }[],
  problemText?: string,
}

interface IOption {
  value: string|null,
  label: string,
}

const ActionModal: React.FC<Props> = (props) => {
  const dispatch = useDispatch();

  // parametros para busca do autocomplete
  const page = 1;
  const limit = 50;
  const orderBy = "name";
  const order = "asc";

  const [isNewData, setIsNewData] = useState(true);
  const [editMode, setEditMode] = useState(false);

  const [actionError, setActionError] = useState(false);
  const [problemError, setProblemError] = useState(false);
  const [uploadSubmitError, setUploadSubmitError] = useState(false)
  const [usersError, setUsersError] = useState(false);

  const [actionText, setActionText] = useState('');
  const [problemText, setProblemText] = useState('');
  const [deadline, setDeadline] = useState(new Date());
  const [uploadedFiles, setUploadedFiles] = useState<IFile[]>([]);

  // selected values on autocomplete
  const [selectedUsers, setSelectedUsers] = useState<IOption[]>([]);

  const [searchTextUsers, setSearchTextUsers] = useState("")

  const [usersFormated, setUsersFormated] = useState([])

  // fetch from BE
  const fetchUsers = useCallback(
    () => dispatch(actions.fetchValidUsers(page, limit, searchTextUsers, order, orderBy)),
    [page, limit, searchTextUsers, order, orderBy]
  );
  const postActions: any = (body: any) => dispatch(actions.postActions(body))


  //get from redux
  const users = useSelector((state: any) => state.users.users);
  const loadingUsers = useSelector((state: any) => state.users.loading);
  const maxAccountablesAllowed = useSelector((state: any) => state.actions.maxAccountablesAllowed)

  //post to BE
  const loadingAction = useSelector((state: any) => state.actions.loading);


  //FUNCTION TO FORMAT FIELD OF ACTIVITIES AND AREAS TYPES IN THE AUTOCOMPLETE
  useEffect(() => {
    setUsersFormated(users.rows.map((user: IUser) => ({ value: user._id, label: user.name })))
  }, [users])


  useEffect(() => {
    fetchUsers();
  }, [searchTextUsers]);

  const handleInputChangeSelectorUsers = (text: string) => {
    setSearchTextUsers(text);
  }

  const handleInputChangeUsers = (selected: IOption[]) => {
    if (selected) setSelectedUsers(selected);
    else setSelectedUsers([]);
    setUsersError(false);
    
  }

  //check if is newEntrance or editing
  useEffect(() => {
    if (props.isOpen) {
      if (!props.isNewData && props.action) {
        setActionText(props.action.actionText);
        setSelectedUsers(props.action.accountables.map((accountable) => ({ value: accountable._id, label: accountable.name })))
        setDeadline(props.action.deadline);
        setIsNewData(false);
        setEditMode(false);
        setProblemText(props.action.problemText || '');
        // Load existing files from the action
        const actionFiles: IFile[] = (props.action.files || [])
          .filter((file: any) => typeof file === 'object' && file !== null)
          .map((file: any) => ({
            ...file,
            id: file.id || file._id,
            progress: 100,
            isNotUploaded: false,
            error: false,
          }));
        setUploadedFiles(actionFiles);
      } else {
        setActionText("");
        setSelectedUsers([])
        setUploadedFiles([]);
        setDeadline(new Date())
        setIsNewData(true);
        setProblemText('');
      }
    }
  }, [props.isOpen])

  const handleSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    let hasError = false;

    if (!actionText) {
      setActionError(true);
      hasError = true
    }

    if (problemText === '') {
      setProblemError(true);
      hasError = true
    }

    const notUploaded = uploadedFiles.filter((fl) => fl.isNotUploaded)
    if (notUploaded.length > 0) {
      setUploadSubmitError(true)
      hasError = true;
    }

    const filesIds: string[] = []

    uploadedFiles.map(fl => {
      if (fl.id)
        return filesIds.push(fl.id as string)
      else if (fl._id)
        return filesIds.push(fl._id)
    });

    if(selectedUsers.length > maxAccountablesAllowed) {
      setUsersError(true)
      hasError = true;
    }

    if (hasError) return;

    let body: IRequestActionBody = {
      _id: props.action?._id || new ObjectID().toHexString(),
      actionText: actionText,
      status: 'progress',
      deadline: deadline,
      files: filesIds,
      question: props.question._id,
      questionText: props.question.name,
      questionGroupName: props.question.groupName,
      accountables: [],
      problemText,
    };

    // se tem um responsavel, vai add ele
    if (selectedUsers.length > 0) {
      body.accountables = selectedUsers.map((accountable) => ({ _id: accountable.value, name: accountable.label }));
    }

    if(!props.serviceId) { // se for false, vai fazer chamada no BE para criar
      // Store full file objects so they can be displayed when editing the action later
      const bodyForRedux = { ...body, files: uploadedFiles as any };
      if (isNewData) {
        dispatch(actions.createServiceAddQuestionAction(bodyForRedux, props.question._id));
      } else if (props.action) {
        dispatch(actions.createServiceEditQuestionAction(bodyForRedux, props.question._id, props.action._id))
      }
      props.createOrUpdateDraft({type: EUpdateDraftType.actions, action: body});
      props.onClose();

    } else {
      const bodyToPost = {
        actionText: body.actionText,
        status: body.status,
        deadline: body.deadline,
        files: body.files,
        question: props.question.questionsId, // nao eh a questao clonada, eh a questao da collection question
        questionText: props.question.name,
        questionGroupName: props.question.groupName,
        service: props.serviceId, // precisa pegar o id do service da url
        accountables: body.accountables,
        problemText: body.problemText,
      }

      postActions(bodyToPost).then((res: any) => {
        if (res !== 'error') {
          body._id = res.actionId
          dispatch(actions.editServiceAddQuestionAction(body, props.question._id))
          props.onClose();
        }
      })
    }
  }

  const handleDateChange = (date: Date) => {
    setDeadline(date);
  }

  const handleUpload = async (files: File[]) => {
    const uploadingFiles = await asyncMap(files, async (file) => {
      const fileOptimized = await resizeFile(file)
  
      return ({
        file: fileOptimized,
        id: uniqueId(),
        name: fileOptimized.name,
        size: fileOptimized.size,
        readableSize: filesize(fileOptimized.size),
        preview: URL.createObjectURL(fileOptimized),
        fileType: fileOptimized.type,
        progress: 0,
        isNotUploaded: true,
        error: false,
        url: "",
      } as IFile);
    });
  
    
    setUploadedFiles((prevUf: IFile[]) => prevUf.concat(uploadingFiles))
    uploadingFiles.forEach((uploadingFile) => processUpload(uploadingFile, updateFile, setUploadSubmitError));
  };

  const updateFile = (id: string, data: any) => {
    setUploadedFiles((prevUf: any) => prevUf.map((uf: any) => id === uf.id ? { ...uf, ...data } : uf))
  };

  const handleDelete = async (id: string) => {
    try {
      await API.delete(`files/${id}`)
      setUploadedFiles(prevUf => prevUf.filter((file: any) => file.id !== id && file._id !== id))

    } catch (e) {
      console.log('e', e);
    }
  }

  const handleDeleteDocument = () => {
    confirm(i18n.t('handleDeleteDocumentModalAction.confirm'),
      {
        title: i18n.t('handleDeleteDocumentModalAction.title'),
        titleColor: 'red',
        disableBackdrop: true
      }).then(
        () => { },
        async () => {
          dispatch(actions.createServiceDeleteQuestionAction(props.question._id, props.action?._id as string));
          props.onClose();
        },
      )
  }

  const handleDeleteDocumentFile = (id: string) => {
    if (!editMode && !isNewData) return;
    confirm(i18n.t('handleDeleteDocumentFileModalAction.confirm'),
      {
        title: i18n.t('handleDeleteDocumentFileModalAction.title'),
        titleColor: 'red',
        disableBackdrop: true
      }).then(
        () => { },
        async () => {
          handleDelete(id)
        },
      )
  }

  return (
    <Dialog
      maxWidth='md'
      fullWidth
      scroll='body'
      open={props.isOpen}
      onClose={() => props.onClose()}
    >
      <Box sx={{ ...styles.opacityContainer, opacity: loadingAction ? '0.3' : '1' }}>
      {loadingAction && <Spinner size={36} overlay />}
        <Box sx={styles.formTitle}>
          <Typography variant='h6'>{isNewData ? i18n.t('typographyModalAction.true') as string : i18n.t('typographyModalAction.false') as string}</Typography>
          {!isNewData &&
            <Box sx={styles.submitButtons}>
              {editMode ?
                <>
                  <IconButton onClick={() => props.onClose()} size="large"><CloseIcon color='error' /></IconButton>
                  <IconButton onClick={handleSubmit} size="large"><DoneIcon sx={{color: 'green'}} /></IconButton>
                </> :
                <>
                  <IconButton onClick={() => setEditMode(true)} size="large"><EditIcon /></IconButton>
                  <IconButton onClick={() => handleDeleteDocument()} size="large"><DeleteIcon /></IconButton>
                </>
              }
            </Box>
          }
        </Box>

        <Typography style={styles.question}>{props.question.name}</Typography>

        <FormControl variant="standard" fullWidth>
          <TextField
            variant="standard"
            autoFocus
            multiline
            disabled={!isNewData && !editMode}
            label={i18n.t('textFieldModalFilter.problem') as string}
            name='problem'
            onChange={event => { setProblemError(false); return setProblemText(event.target.value.toUpperCase()) }}
            value={problemText}
            error={problemError}
            margin='normal' />
        </FormControl>

        <FormControl variant="standard" fullWidth>
          <TextField
            variant="standard"
            multiline
            disabled={!isNewData && !editMode}
            label={i18n.t('textFieldModalFilter.action') as string}
            name='action'
            onChange={event => { setActionError(false); return setActionText(event.target.value.toUpperCase()) }}
            value={actionText}
            error={actionError}
            margin='normal' />
        </FormControl>

        <FormControl variant="standard" fullWidth margin='normal'>
          <Autocomplete
            loading={loadingUsers} //sent to isLoading
            label={i18n.t('autocompleteModalAction.user') as string}
            data={usersFormated} // sent to options
            onSelectValue={handleInputChangeUsers} // sent to onChange
            valueSelected={selectedUsers} // sent to value
            newSearchText={handleInputChangeSelectorUsers}
            name='funcao'
            hasError={usersError}
            disabled={!isNewData && !editMode}
            isMulti
          />
          {usersError && <FormHelperText sx={{color: '#d32f2f'}}>{`${i18n.t('autocompleteModalAction.helperText')} ${maxAccountablesAllowed}` as string}</FormHelperText>}
        </FormControl>

        <DatePicker
          label={i18n.t('keyboardDatePicker.deadline') as string }
          value={new Date(deadline)}
          onChange={date => handleDateChange(date as Date)}
          minDate={new Date()}
          format="dd/MM/yyyy"
          disabled={!isNewData && !editMode}
          slotProps={{ textField: { variant: 'standard', } }}
          sx={{marginTop: '18px'}}
        />

        <div style={styles.sectionTitleContainer}>
          <AttachIcon style={{ color: (!isNewData && !editMode) ? 'lightgrey' : '#000' }} />
          <Typography style={{ color: (!isNewData && !editMode) ? 'lightgrey' : '#000' }}>{i18n.t('typographyModalAction.text') as string}</Typography>
        </div>
        <Upload onUpload={handleUpload} disabled={!isNewData && !editMode} />
        {!!uploadedFiles.length && (
          <FileList files={uploadedFiles} onDelete={handleDeleteDocumentFile} disabled={!editMode && !isNewData} />
        )}
        {uploadSubmitError && <div style={styles.fileError}>{i18n.t('divModalAction.fileError') as string}</div>}

        {isNewData &&
          <Box sx={styles.submitButtons}>
            <IconButton onClick={() => props.onClose()} size="large"><CloseIcon fontSize='large' color='error' /></IconButton>
            <IconButton onClick={handleSubmit} size="large"><DoneIcon sx={{color: 'green'}} fontSize='large'/></IconButton>
          </Box>
        }

      </Box>
    </Dialog>
  );
}

export default ActionModal;


const styles = {
  submitButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '20px 0',
  },
  formTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  container: {
    padding: '10px',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  print: {
    marginRight: 5,
  },
  contentContainer: {
    margin: '30px 0',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  opacityContainer: {
    position: 'relative',
    padding: '20px',
  },
  fileError: {
    color: '#f44336'
  },
  question: {
    marginTop: 20,
  },
  sectionTitleContainer: {
    display: 'flex',
    marginTop: 20,
  },
}
